import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getToken, getUserId, isSuperAdmin } from '../utils/auth';
import salesOpsLogo from '../assets/salesops-logo.png';
import sphereRight from '../assets/login-sphere-right.png';
import './Login.css';

// İlk giriş zorunlu şifre değişikliği ekranı.
// Kullanıcı sistemin ürettiği geçici şifreyle giriş yaptığında (LoginUserInfo.mustChangePassword)
// login akışı buraya yönlendirir. Geçici (mevcut) şifre login ekranından state ile taşınır; kullanıcı
// yalnız yeni şifresini belirler. Başarılı değişimde MustChangePassword backend'de temizlenir.
export function ForcePasswordChangePage() {
  const navigate = useNavigate();
  const location = useLocation();

  // Geçici şifre login'den taşındıysa kullanıcı tekrar yazmaz; taşınmadıysa (örn. sayfa yenilendi)
  // kullanıcıdan mevcut geçici şifresini isteriz.
  const carriedCurrent = location.state?.currentPassword || '';
  const [current, setCurrent] = useState(carriedCurrent);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const userId = getUserId();
    if (!userId) { setError('Oturum bulunamadı. Lütfen tekrar giriş yapın.'); return; }
    if (!current) { setError('Geçici şifrenizi giriniz.'); return; }
    if (password !== confirm) { setError('Şifreler eşleşmiyor.'); return; }
    if (password === current) { setError('Yeni şifre geçici şifreden farklı olmalıdır.'); return; }
    setLoading(true);
    try {
      const res = await fetch('/api/Users/ChangePassword', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
        },
        body: JSON.stringify({ userId, currentPassword: current, newPassword: password }),
      });
      const body = await res.json().catch(() => null);
      const ok = res.ok && (body?.success ?? body?.Success) !== false;
      if (ok) {
        // Depolanan kullanıcı bilgisindeki bayrağı temizle ki App.js tekrar buraya yönlendirmesin.
        try {
          const stored = JSON.parse(localStorage.getItem('user') || '{}');
          stored.mustChangePassword = false;
          localStorage.setItem('user', JSON.stringify(stored));
        } catch { /* yoksay */ }
        navigate(isSuperAdmin() ? '/tenants' : '/dashboards/general', { replace: true });
      } else {
        setError((body?.message ?? body?.Message) || 'Şifre değiştirilemedi. Lütfen tekrar deneyin.');
      }
    } catch {
      setError('Bağlantı hatası. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const eyeToggle = (
    <button
      type="button"
      className="login-eye"
      onClick={() => setShowPassword((v) => !v)}
      aria-label={showPassword ? 'Şifreyi gizle' : 'Şifreyi göster'}
      aria-pressed={showPassword}
      tabIndex={-1}
    >
      {showPassword ? (
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 10 8 10 8a18.5 18.5 0 0 1-2.16 3.19M6.61 6.61A18.5 18.5 0 0 0 2 12s3 8 10 8a9.12 9.12 0 0 0 5.39-1.61" />
          <path d="M9.88 9.88a3 3 0 0 0 4.24 4.24" />
          <line x1="2" y1="2" x2="22" y2="22" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M2 12s3-8 10-8 10 8 10 8-3 8-10 8-10-8-10-8Z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      )}
    </button>
  );

  return (
    <main className="login-screen" role="main" aria-label="İlk Giriş Şifre Değişikliği">
      <div className="login-bg" aria-hidden="true">
        <div className="login-orb-left" />
      </div>

      <div className="login-stage">
        <div className="login-brand" aria-hidden="true">
          <img className="login-brand-logo" src={salesOpsLogo} alt="SalesOps" />
          <small className="login-brand-tag">SALES OPERATIONS WORKSTATION</small>
        </div>

        <div className="login-cardbox">
          <img className="login-orb-right" src={sphereRight} alt="" aria-hidden="true" />
          <div className="login-card">
            <img className="login-logo" src={salesOpsLogo} alt="SalesOps" />

            <header className="login-card-head">
              <h1>Şifrenizi Belirleyin</h1>
              <p>İlk girişiniz için lütfen yeni bir şifre belirleyin.</p>
            </header>

          <form onSubmit={handleSubmit} autoComplete="off">
            {!carriedCurrent && (
              <div className="login-field">
                <label htmlFor="fp-current">Geçici Şifre</label>
                <div className="login-input">
                  <input
                    id="fp-current"
                    type={showPassword ? 'text' : 'password'}
                    name="current-password"
                    value={current}
                    onChange={(e) => setCurrent(e.target.value)}
                    autoComplete="current-password"
                    required
                  />
                </div>
              </div>
            )}

            <div className="login-field">
              <label htmlFor="fp-password">Yeni Şifre</label>
              <div className="login-input">
                <input
                  id="fp-password"
                  type={showPassword ? 'text' : 'password'}
                  name="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
                {eyeToggle}
              </div>
            </div>

            <div className="login-field">
              <label htmlFor="fp-confirm">Yeni Şifre (Tekrar)</label>
              <div className="login-input">
                <input
                  id="fp-confirm"
                  type={showPassword ? 'text' : 'password'}
                  name="confirm-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </div>
            </div>

            <button type="submit" className="login-submit" disabled={loading}>
              {loading ? 'Kaydediliyor...' : 'Şifreyi Belirle ve Devam Et'}
            </button>

            <div className="login-foot">
              <div className="login-msg" aria-live="polite">{error}</div>
            </div>
          </form>
          </div>
        </div>
      </div>
    </main>
  );
}

export default ForcePasswordChangePage;
